import { showMessages } from '@intercom/messenger-js-sdk';
import { BellIcon } from '@synoptic/ui-kit/icons/react/bell.js';
import { CandlestickChartIcon } from '@synoptic/ui-kit/icons/react/candlestick-chart.js';
import { DataTransferBothIcon } from '@synoptic/ui-kit/icons/react/data-transfer-both.js';
import { GraphUpIcon } from '@synoptic/ui-kit/icons/react/graph-up.js';
import { HomeIcon } from '@synoptic/ui-kit/icons/react/home.js';
import { MessageIcon } from '@synoptic/ui-kit/icons/react/message.js';
import { PlanetIcon } from '@synoptic/ui-kit/icons/react/planet.js';
import { RocketIcon } from '@synoptic/ui-kit/icons/react/rocket.js';
import { SearchIcon } from '@synoptic/ui-kit/icons/react/search.js';
import { SettingsIcon } from '@synoptic/ui-kit/icons/react/settings.js';
import { ToolsIcon } from '@synoptic/ui-kit/icons/react/tools.js';
import { UserStarIcon } from '@synoptic/ui-kit/icons/react/user-star.js';
import { ViewColumns3Icon } from '@synoptic/ui-kit/icons/react/view-columns-3.js';
import { ReactNode } from 'react';
import { adminPaths, paths } from '@/routes-utils/paths.ts';

export type NavItemIdV2 =
  | 'home'
  | 'marketplace'
  | 'newsDeck'
  | 'terminal'
  | 'learningCenter'
  | 'tools'
  | 'charts'
  | 'notifications'
  | 'enterprise'
  | 'contact'
  | 'search'
  | 'sources'
  | 'admin';

export type NavBadgeV2 = 'New' | 'Beta';

type NavTargetV2 =
  | { to: string; activePrefix?: string; action?: never }
  | { to?: never; activePrefix?: never; action: () => void };

export type NavItemDefV2 = {
  label: string;
  icon: ReactNode;
  badge?: NavBadgeV2;
  adminOnly?: boolean;
} & NavTargetV2;

export const navItemsV2 = {
  home: { label: 'Home', icon: <HomeIcon />, to: paths.home },
  marketplace: {
    label: 'Marketplace',
    icon: <PlanetIcon />,
    to: paths.streams,
    activePrefix: paths.marketplace,
  },
  newsDeck: {
    label: 'NewsDeck',
    icon: <ViewColumns3Icon />,
    to: paths.newsDeck,
  },
  terminal: {
    label: 'Terminal',
    icon: <GraphUpIcon />,
    to: paths.terminal,
    badge: 'Beta',
  },
  learningCenter: {
    label: 'Learning Center',
    icon: <RocketIcon />,
    to: paths.learningCenter,
    badge: 'New',
  },
  tools: {
    label: 'Tools',
    icon: <ToolsIcon />,
    to: paths.tools,
  },
  charts: {
    label: 'My Charts',
    icon: <CandlestickChartIcon />,
    to: paths.myCharts,
  },
  notifications: {
    label: 'Notifications',
    icon: <BellIcon />,
    to: paths.notifications,
  },
  enterprise: {
    label: 'Enterprise',
    icon: <UserStarIcon />,
    to: paths.enterprise,
  },
  contact: {
    label: 'Contact us',
    icon: <MessageIcon />,
    action: () => showMessages(),
  },
  search: { label: 'Search', icon: <SearchIcon />, to: paths.search },
  sources: {
    label: 'My Sources',
    icon: <DataTransferBothIcon />,
    to: adminPaths.sources,
    adminOnly: true,
  },
  admin: {
    label: 'Admin dashboard',
    icon: <SettingsIcon />,
    to: adminPaths.dashboard,
    adminOnly: true,
  },
} satisfies Record<NavItemIdV2, NavItemDefV2>;

export type NavSectionDefV2 = {
  id: string;
  label?: string;
  items: readonly NavItemIdV2[];
};

export const sidebarSectionsV2: readonly NavSectionDefV2[] = [
  { id: 'primary', items: ['home', 'terminal', 'marketplace', 'newsDeck'] },
  {
    id: 'workspace',
    label: 'WORKSPACE',
    items: [
      'learningCenter',
      'tools',
      'charts',
      'notifications',
      'enterprise',
      'contact',
    ],
  },
  {
    id: 'admin',
    label: 'ADMIN',
    items: ['sources', 'admin'],
  },
];

export const mobileBarItemsV2: readonly NavItemIdV2[] = [
  'home',
  'search',
  'notifications',
];

export const mobileMoreItemsV2: readonly NavItemIdV2[] = [
  'terminal',
  'marketplace',
  'newsDeck',
  'learningCenter',
  'tools',
  'charts',
  'enterprise',
  'contact',
  'sources',
  'admin',
];

export const visibleNavItemsV2 = (
  items: readonly NavItemIdV2[],
  isAdmin: boolean,
) =>
  items.filter((id) => {
    const def: NavItemDefV2 = navItemsV2[id];
    return isAdmin || !def.adminOnly;
  });
